import React, { useEffect, useState } from 'react';
import { ArrowLeft, Star, MapPin, Clock, Phone, Heart, Copy, Gift, Utensils, Award, Coffee, Check, X, BookOpen, ShoppingBag, Crown } from 'lucide-react';
import { Store } from '../../types';
import { Button } from '../ui/Button';
import { LoyaltyCard } from '../LoyaltyCard';
import { useToast } from '../ToastContext';
interface StoreProfileScreenProps {
  store: Store;
  onBack: () => void;
  onViewMenu: () => void;
  onCheckIn: () => void;
  onGift: (storeId: string, storeName: string, storeCode: string) => void;
  isImHereFlow?: boolean;
}
export function StoreProfileScreen({
  store,
  onBack,
  onViewMenu,
  onCheckIn,
  onGift,
  isImHereFlow = false
}: StoreProfileScreenProps) {
  const [showLoyalty, setShowLoyalty] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showStamps, setShowStamps] = useState(false);
  const [showRewardMessage, setShowRewardMessage] = useState(true);
  const {
    showToast
  } = useToast();
  // Flip to membership card after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowRewardMessage(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);
  const handleCopyId = () => {
    navigator.clipboard.writeText(store.storeId);
    showToast('Store ID copied!', 'success');
  };
  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
    showToast(isFavorite ? 'Removed from favorites' : 'Added to favorites', 'info');
  };
  const handleGiftClick = () => {
    onGift(store.id, store.name, store.storeId);
    showToast(`Let's send something special from ${store.name}! 🎁`, 'success');
  };
  // Determine button text and icon based on store type
  const getViewButtonContent = () => {
    const tags = store.tags.map(tag => tag.toLowerCase());
    if (tags.includes('books')) {
      return {
        icon: BookOpen,
        text: 'View Collections'
      };
    } else if (tags.includes('coffee') || tags.includes('food') || tags.includes('pizza') || tags.includes('bakery')) {
      return {
        icon: Utensils,
        text: 'View Menu & Order'
      };
    } else {
      return {
        icon: ShoppingBag,
        text: 'Browse Products'
      };
    }
  };
  const viewButtonContent = getViewButtonContent();
  const ViewIcon = viewButtonContent.icon;
  const stamps = 7;
  const totalStamps = 10;
  return <div className="pb-24 bg-gradient-to-b from-purple-50 to-white min-h-screen animate-in slide-in-from-right duration-300" data-id="element-1187">
      {/* Hero Image */}
      <div className="relative h-64" data-id="element-1188">
        <img src={store.image} alt={store.name} className="w-full h-full object-cover" data-id="element-1189" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" data-id="element-1190" />

        <button onClick={onBack} className="absolute top-6 left-4 z-10 bg-[#FFFC00] rounded-full p-3 shadow-lg hover:bg-[#F7C300] transition-all active:scale-95" data-id="element-1191">
          <ArrowLeft size={24} className="text-black" data-id="element-1192" />
        </button>

        <div className="absolute bottom-0 left-0 right-0 p-6 text-white" data-id="element-1193">
          <div className="flex justify-between items-end mb-2" data-id="element-1194">
            <h1 className="text-3xl font-bold leading-tight" data-id="element-1195">{store.name}</h1>
            <div className="flex items-center gap-1 bg-yellow-400 text-yellow-900 px-3 py-1.5 rounded-xl font-bold text-sm shadow-lg" data-id="element-1196">
              <Star size={14} fill="currentColor" data-id="element-1197" />
              {store.rating}
            </div>
          </div>
          <p className="text-white/90 flex items-center gap-2 text-sm" data-id="element-1198">
            <MapPin size={14} data-id="element-1199" /> {store.address} • {store.distance}
          </p>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6" data-id="element-1200">
        {/* I'm Here Mode Banner */}
        {isImHereFlow && <div className="bg-gradient-to-r from-emerald-500 to-green-500 rounded-2xl p-4 shadow-lg animate-in fade-in slide-in-from-top duration-500" data-id="element-1201">
            <div className="flex items-center gap-3" data-id="element-1202">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-md" data-id="element-1203">
                <Check size={24} className="text-emerald-600" strokeWidth={3} data-id="element-1204" />
              </div>
              <div data-id="element-1205">
                <p className="font-bold text-white text-lg" data-id="element-1206">You're here!</p>
                <p className="text-sm text-emerald-50" data-id="element-1207">
                  Browse the menu to order
                </p>
              </div>
            </div>
          </div>}

        {/* Store ID & Favorite Row */}
        <div className="flex items-center gap-3" data-id="element-1208">
          <div className="flex-1 bg-white rounded-2xl p-4 shadow-sm border border-purple-100" data-id="element-1209">
            <div className="flex items-center justify-between" data-id="element-1210">
              <div data-id="element-1211">
                <p className="text-xs text-purple-600 uppercase font-bold mb-1" data-id="element-1212">
                  Store ID
                </p>
                <p className="font-mono font-bold text-gray-900 text-sm" data-id="element-1213">
                  {store.storeId}
                </p>
              </div>
              <button onClick={handleCopyId} className="p-2.5 text-purple-400 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-all active:scale-95" title="Copy Store ID" data-id="element-1214">
                <Copy size={18} data-id="element-1215" />
              </button>
            </div>
          </div>
          <button onClick={toggleFavorite} className={`p-4 rounded-2xl shadow-sm transition-all active:scale-95 ${isFavorite ? 'bg-gradient-to-br from-red-500 to-pink-500 text-white shadow-red-200' : 'bg-white border border-purple-100 text-gray-400 hover:text-red-500 hover:border-red-200'}`} title={isFavorite ? 'Remove from favorites' : 'Add to favorites'} data-id="element-1216">
            <Heart size={24} fill={isFavorite ? 'currentColor' : 'none'} strokeWidth={2} data-id="element-1217" />
          </button>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-3" data-id="element-1218">
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-white shadow-sm border border-purple-100" data-id="element-1219">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-1220">
              <Clock size={20} className="text-purple-600" data-id="element-1221" />
            </div>
            <div data-id="element-1222">
              <p className="text-xs font-bold text-gray-900 mb-0.5" data-id="element-1223">
                Open Today
              </p>
              <p className="text-xs text-gray-600" data-id="element-1224">{store.hours}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-white shadow-sm border border-purple-100" data-id="element-1225">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-1226">
              <Phone size={20} className="text-purple-600" data-id="element-1227" />
            </div>
            <div data-id="element-1228">
              <p className="text-xs font-bold text-gray-900 mb-0.5" data-id="element-1229">Contact</p>
              <p className="text-xs text-gray-600" data-id="element-1230">{store.phone}</p>
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2" data-id="element-1231">
          {store.tags.map(tag => <span key={tag} className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-full text-xs font-semibold shadow-sm" data-id="element-1232">
              {tag}
            </span>)}
        </div>

        {/* Flip Card: Reward Message → Membership Card */}
        <div className="relative h-32" style={{
        perspective: '1000px'
      }} data-id="element-1233">
          <div className={`relative w-full h-full transition-all duration-700 ease-in-out`} style={{
          transformStyle: 'preserve-3d',
          transform: showRewardMessage ? 'rotateY(0deg)' : 'rotateY(180deg)'
        }} data-id="element-1234">
            {/* Front: Reward Message */}
            <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl p-5 shadow-lg" style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden'
          }} data-id="element-1235">
              <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl p-4 h-full flex flex-col justify-center" data-id="element-1236">
                <p className="text-white text-base font-bold mb-2" data-id="element-1237">
                  Next time, your croissant's on us 🍪
                </p>
                <div className="flex items-center gap-2 text-white/90 text-sm" data-id="element-1238">
                  <Clock size={14} data-id="element-1239" />
                  <span data-id="element-1240">Available until May 15</span>
                </div>
              </div>
            </div>

            {/* Back: Membership Card */}
            <div className="absolute inset-0 bg-gradient-to-br from-red-900 via-red-600 to-amber-500 rounded-2xl p-5 shadow-lg" style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)'
          }} data-id="element-1241">
              <div className="h-full flex flex-col justify-between" data-id="element-1242">
                <div data-id="element-1243">
                  <div className="flex items-center gap-2 mb-2" data-id="element-1244">
                    <div className="text-3xl" data-id="element-1245">👑</div>
                    <div data-id="element-1246">
                      <h3 className="text-amber-200 font-bold text-xl leading-tight" data-id="element-1247">
                        MAHARAJA MEMBERSHIP
                      </h3>
                      <p className="text-amber-100/80 text-sm" data-id="element-1248">
                        Royal treatment, every visit
                      </p>
                    </div>
                  </div>
                </div>
                <button onClick={() => showToast('Maharaja Membership coming soon! 👑', 'info')} className="w-full bg-gradient-to-r from-amber-400 to-yellow-300 text-red-900 py-3 rounded-xl font-bold text-base hover:from-amber-300 hover:to-yellow-200 transition-all active:scale-95 shadow-lg" data-id="element-1249">
                  Join the Royal Circle - ₹999/year
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Actions - Uniform Spacing with Corrected Order */}
        <div className="space-y-3" data-id="element-1250">
          {/* Rewards Tracker Button - NOW FIRST */}
          <button onClick={() => setShowStamps(true)} className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white font-bold py-4 rounded-2xl transition-all active:scale-[0.98] shadow-lg shadow-purple-200 hover:shadow-xl hover:shadow-purple-300 flex items-center justify-center gap-2" data-id="element-1251">
            <Award size={20} data-id="element-1252" />
            <span className="text-sm uppercase tracking-wider" data-id="element-1253">
              Rewards Tracker
            </span>
          </button>

          {/* View Menu Button - NOW SECOND */}
          <button onClick={onViewMenu} className="w-full bg-[#FFFC00] text-black py-4 rounded-2xl font-bold text-base hover:bg-[#F7C300] transition-all active:scale-[0.98] shadow-lg shadow-yellow-200 hover:shadow-xl hover:shadow-yellow-300 flex items-center justify-center gap-2" data-id="element-1254">
            <ViewIcon size={20} data-id="element-1255" />
            {viewButtonContent.text}
          </button>

          {/* Show different secondary action based on mode */}
          {isImHereFlow ?
        // I'm Here mode: Already checked in
        <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-2xl p-4 text-center" data-id="element-1256">
              <p className="text-sm font-semibold text-emerald-900" data-id="element-1257">
                You've earned loyalty points! 🎉
              </p>
              <p className="text-xs text-emerald-700 mt-1" data-id="element-1258">
                Keep shopping to unlock more rewards
              </p>
            </div> :
        // Browse mode: Show I'm Here option
        <button onClick={onCheckIn} className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white py-4 rounded-2xl font-bold text-base hover:from-purple-700 hover:to-purple-800 transition-all active:scale-[0.98] shadow-lg shadow-purple-200 hover:shadow-xl hover:shadow-purple-300 flex items-center justify-center gap-2" data-id="element-1259">
              <MapPin size={20} data-id="element-1260" />
              I'm Here
            </button>}

          {/* Enhanced Gift Button */}
          <button onClick={handleGiftClick} className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white py-5 rounded-2xl font-semibold hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex flex-col items-center justify-center shadow-lg shadow-pink-200 hover:shadow-xl hover:shadow-pink-300" data-id="element-1261">
            <span className="text-lg font-bold flex items-center gap-2" data-id="element-1262">
              <Gift size={20} data-id="element-1263" />
              Surprise a Friend
            </span>
            <span className="text-xs text-white/80 font-normal mt-1" data-id="element-1264">
              Coffee, treats, or perks — sent instantly
            </span>
          </button>
        </div>
      </div>

      {/* Rewards Tracker Modal */}
      {showStamps && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setShowStamps(false)} data-id="element-1265">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl animate-in slide-in-from-bottom duration-300" onClick={e => e.stopPropagation()} data-id="element-1266">
            {/* Header */}
            <div className="flex items-center justify-between mb-4" data-id="element-1267">
              <h3 className="text-xl font-bold text-gray-900" data-id="element-1268">Your Progress</h3>
              <button onClick={() => setShowStamps(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-1269">
                <X size={20} className="text-gray-600" data-id="element-1270" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-6" data-id="element-1271">
              {totalStamps - stamps} more visits until your free reward!
            </p>

            {/* Stamps Grid */}
            <div className="grid grid-cols-5 gap-3 mb-5" data-id="element-1272">
              {Array.from({
            length: totalStamps
          }).map((_, i) => {
            const isStamped = i < stamps;
            return <div key={i} className={`
                      aspect-square rounded-full flex items-center justify-center border-2 transition-all duration-500
                      ${isStamped ? 'bg-gradient-to-br from-yellow-400 to-amber-500 border-yellow-500 text-white scale-100 shadow-md' : 'bg-gray-100 border-gray-200 text-gray-400 scale-90'}
                    `} data-id="element-1273">
                    {isStamped ? <Check size={16} strokeWidth={3} data-id="element-1274" /> : <Coffee size={16} data-id="element-1275" />}
                  </div>;
          })}
            </div>

            {/* Progress Bar */}
            <div className="bg-gray-100 rounded-full h-2 overflow-hidden mb-2" data-id="element-1276">
              <div className="bg-gradient-to-r from-yellow-400 to-amber-500 h-full transition-all duration-500 rounded-full" style={{
            width: `${stamps / totalStamps * 100}%`
          }} data-id="element-1277" />
            </div>
            <p className="text-center text-gray-600 text-xs mb-6" data-id="element-1278">
              {stamps} / {totalStamps} stamps collected
            </p>

            {/* Close Button */}
            <button onClick={() => setShowStamps(false)} className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white py-3 rounded-2xl font-semibold hover:from-purple-700 hover:to-purple-800 transition-all active:scale-[0.98]" data-id="element-1279">
              Done
            </button>
          </div>
        </div>}

      {showLoyalty && <LoyaltyCard stamps={stamps} storeName={store.name} onClose={() => setShowLoyalty(false)} data-id="element-1280" />}
    </div>;
}